import React, { useEffect, useState } from "react";
import { api, resolveImage } from "@/lib/api";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";

const EMPTY = {
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  price: 0,
  mileage: 0,
  fuel: "Petrol",
  transmission: "Automatic",
  status: "available",
  description: "",
  images: [],
  featured: false,
};

export default function AdminVehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/vehicles")
      .then((r) => setVehicles(r.data || []))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this vehicle?")) return;
    try {
      await api.delete(`/admin/vehicles/${id}`);
      toast.success("Vehicle deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  return (
    <div data-testid="admin-vehicles">
      <div className="flex items-end justify-between gap-6 mb-8 flex-wrap">
        <div>
          <div className="section-label mb-3">Garage</div>
          <h1 className="font-display text-3xl md:text-4xl font-light">Vehicles</h1>
        </div>
        <button
          className="btn-gold"
          onClick={() => { setEditing(null); setOpen(true); }}
          data-testid="add-vehicle-btn"
        >
          <Plus size={14} /> Add Vehicle
        </button>
      </div>

      <div className="border border-white/5 bg-[#0F0F0F] overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm" data-testid="vehicles-table">
          <thead>
            <tr className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 border-b border-white/5">
              <th className="text-left px-5 py-4">Vehicle</th>
              <th className="text-left px-5 py-4">Year</th>
              <th className="text-left px-5 py-4">Price</th>
              <th className="text-left px-5 py-4">Status</th>
              <th className="text-right px-5 py-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-5 py-10 text-center text-zinc-500">Loading…</td></tr>
            )}
            {!loading && vehicles.map((v) => (
              <tr key={v.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-10 bg-[#1A1A1A] overflow-hidden flex-shrink-0">
                      {v.images?.[0] && <img src={resolveImage(v.images[0])} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.28em] uppercase text-[#C6A15B]">{v.brand}</div>
                      <div className="text-white text-sm font-medium">{v.model}</div>
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-zinc-300">{v.year}</td>
                <td className="px-5 py-4 text-white">€{(v.price || 0).toLocaleString()}</td>
                <td className="px-5 py-4">
                  <span className={`status-chip status-${v.status}`}>{v.status}</span>
                </td>
                <td className="px-5 py-4 text-right">
                  <button
                    onClick={() => { setEditing(v); setOpen(true); }}
                    className="inline-flex items-center justify-center w-8 h-8 border border-white/10 text-zinc-300 hover:border-[#C6A15B] hover:text-[#C6A15B] mr-2"
                    data-testid={`edit-vehicle-${v.id}`}
                    title="Edit"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => onDelete(v.id)}
                    className="inline-flex items-center justify-center w-8 h-8 border border-white/10 text-zinc-300 hover:border-red-500 hover:text-red-500"
                    data-testid={`delete-vehicle-${v.id}`}
                    title="Delete"
                  >
                    <Trash2 size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {!loading && vehicles.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-12 text-center text-zinc-500">No vehicles yet. Add your first one.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <VehicleFormModal
          initial={editing}
          onClose={() => setOpen(false)}
          onSaved={() => { setOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function VehicleFormModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === "checkbox" ? checked : value;
    if (["year", "price", "mileage"].includes(name)) val = Number(value) || 0;
    setForm((f) => ({ ...f, [name]: val }));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...(f.images || []), r.data.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial?.id) {
        await api.patch(`/admin/vehicles/${initial.id}`, form);
        toast.success("Vehicle updated");
      } else {
        await api.post("/admin/vehicles", form);
        toast.success("Vehicle created");
      }
      onSaved();
    } catch (err) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="vehicle-form-modal">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-3xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5">
          <h2 className="font-display text-xl">{initial ? "Edit vehicle" : "Add vehicle"}</h2>
          <button onClick={onClose} className="p-2 text-zinc-400 hover:text-white" data-testid="close-modal">
            <X size={16} />
          </button>
        </div>
        <form onSubmit={onSubmit} className="p-6 space-y-5" data-testid="vehicle-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <Field label="Brand"><input name="brand" value={form.brand} onChange={onChange} required className="ec-input" data-testid="vf-brand" /></Field>
            <Field label="Model"><input name="model" value={form.model} onChange={onChange} required className="ec-input" data-testid="vf-model" /></Field>
            <Field label="Year"><input type="number" name="year" value={form.year} onChange={onChange} required className="ec-input" /></Field>
            <Field label="Price (EUR)"><input type="number" name="price" value={form.price} onChange={onChange} required className="ec-input" /></Field>
            <Field label="Mileage (km)"><input type="number" name="mileage" value={form.mileage} onChange={onChange} className="ec-input" /></Field>
            <Field label="Fuel">
              <select name="fuel" value={form.fuel} onChange={onChange} className="ec-input">
                <option>Petrol</option><option>Diesel</option><option>Hybrid</option><option>Electric</option>
              </select>
            </Field>
            <Field label="Transmission">
              <select name="transmission" value={form.transmission} onChange={onChange} className="ec-input">
                <option>Automatic</option><option>Manual</option><option>PDK</option><option>DCT</option>
              </select>
            </Field>
            <Field label="Status">
              <select name="status" value={form.status} onChange={onChange} className="ec-input" data-testid="vf-status">
                <option value="available">Available</option>
                <option value="reserved">Reserved</option>
                <option value="sold">Sold</option>
              </select>
            </Field>
          </div>

          <Field label="Description">
            <textarea name="description" value={form.description || ""} onChange={onChange} rows={3} className="ec-input resize-none" />
          </Field>

          <div>
            <div className="ec-label">Images</div>
            <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
              {(form.images || []).map((src, i) => (
                <div key={i} className="relative aspect-[4/3] bg-[#1A1A1A] overflow-hidden">
                  <img src={resolveImage(src)} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/70 text-white flex items-center justify-center hover:bg-red-500"
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <label className="aspect-[4/3] border border-dashed border-white/15 flex flex-col items-center justify-center text-xs text-zinc-400 hover:border-[#C6A15B] hover:text-[#C6A15B] cursor-pointer" data-testid="upload-image-btn">
                <Upload size={16} />
                <span className="mt-2 tracking-widest uppercase text-[10px]">{uploading ? "Uploading…" : "Upload"}</span>
                <input type="file" accept="image/*" onChange={onUpload} className="hidden" disabled={uploading} />
              </label>
            </div>
          </div>

          <label className="flex items-center gap-3 text-sm text-zinc-300">
            <input type="checkbox" name="featured" checked={!!form.featured} onChange={onChange} className="accent-[#C6A15B]" />
            Show in Featured section on homepage
          </label>

          <div className="flex justify-end gap-3 pt-4 border-t border-white/5">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-gold" disabled={saving} data-testid="save-vehicle">
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="ec-label">{label}</label>
      {children}
    </div>
  );
}
